import VideoAttribution from "@/components/VideoAttribution";
import { useRead, ReadState } from "@/components/LoadingStates";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SectionTabs, { SECTION_TABS } from "@/components/SectionTabs";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PlatformBadge from "@/components/PlatformBadge";
import MediaImage from "@/components/MediaImage";
import { explicitDemoMedia } from "@/lib/media";
import ExportButton from "@/components/ExportButton";
import DecisionSummary from "@/components/DecisionSummary";
import FollowUpControls from "@/components/FollowUpControls";
import { useInvalidateMyFollowUps } from "@/hooks/useMyFollowUps";
import { useRealtime } from "@/context/RealtimeContext";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { classNames, formatNumber, timeAgo, platformLabel } from "@/lib/format";
import { Gavel, Search, MessagesSquare, Zap, PinOff, CalendarRange, UserCheck, BookmarkX, VideoOff } from "lucide-react";
import ConfirmAction, { describeFollowUp } from "@/components/ConfirmAction";
import { focusTemporarily } from "@/lib/focus";
import { isDecisionItem, splitItems } from "@/lib/decisionItems";

const RANGES = [
  { key: "all", label: "All time" },
  { key: "7", label: "7d" },
  { key: "30", label: "30d" },
  { key: "90", label: "90d" },
  { key: "custom", label: "Custom" },
];

const STATUSES = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "due_soon", label: "Due soon" },
  { key: "overdue", label: "Overdue" },
  { key: "done", label: "Done" },
  { key: "unassigned", label: "Unassigned" },
];

const stateOf = (fu) => {
  if (!fu) return "none";
  if (fu.status === "done") return "done";
  if (!fu.due_date) return "open";
  const n = Math.round((new Date(`${fu.due_date}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
  return n < 0 ? "overdue" : n <= 3 ? "due_soon" : "open";
};

const pill = (active) => classNames("px-3 py-1.5 text-xs rounded-md transition-colors font-medium", active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground");

function ViewerAvatars({ viewers }) {
  if (!viewers || viewers.length === 0) return null;
  return (
    <span className="ml-auto flex items-center gap-1.5" data-testid="decision-viewers" title={`${viewers.map((v) => v.name).join(", ")} viewing this decision`}>
      <span className="flex -space-x-1.5">
        {viewers.slice(0, 3).map((v) => (
          <span
            key={v.id}
            data-testid={`decision-viewer-${v.id}`}
            className="relative flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--emerald))]/15 text-[hsl(var(--emerald))] font-semibold text-[9px] ring-2 ring-background"
          >
            {v.name?.[0]?.toUpperCase() || "U"}
          </span>
        ))}
      </span>
      <span className="text-[10px] text-[hsl(var(--emerald))] font-medium hidden sm:inline">viewing</span>
    </span>
  );
}

function DecisionCard({ d, canEdit, onUnpin, members, onFollowUp, highlighted, viewers, currentUserId }) {
  const unavailable = d.video_available === false;
  return (
    <Card data-decision-id={d.id} className={classNames("p-4 flex gap-4 border-l-4 transition-shadow duration-500", highlighted && "ring-2 ring-[hsl(var(--amber))] ring-offset-2 ring-offset-background", d.follow_up_state === "overdue" ? "border-l-[hsl(var(--rose))]" : d.follow_up_state === "done" ? "border-l-[hsl(var(--emerald))]" : "border-l-[hsl(var(--amber))]")} data-testid={`decision-card-${d.id}`}>
      {unavailable ? (
        <div className="relative shrink-0 w-20 sm:w-24 aspect-[9/12] rounded-lg overflow-hidden bg-muted flex items-center justify-center text-muted-foreground" data-testid={`decision-unavailable-media-${d.id}`}>
          <VideoOff className="h-6 w-6" aria-hidden="true" />
        </div>
      ) : (
      <Link to={d.thread_path} className="relative shrink-0 w-20 sm:w-24 aspect-[9/12] rounded-lg overflow-hidden bg-muted">
        <MediaImage src={d.thumbnail} alt="" demo={explicitDemoMedia(d)} showLabel={false} className="h-full w-full object-cover" />
        {d.is_outlier && (
          <span className="absolute top-1 right-1 flex items-center gap-0.5 rounded-full bg-[hsl(var(--emerald))] text-white text-[9px] font-bold px-1.5 py-0.5"><Zap className="h-2.5 w-2.5" />{Number(d.outlier_multiplier).toFixed(1)}x</span>
        )}
      </Link>
      )}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--amber))]">
          <Gavel className="h-3 w-3" /> Decision
          <span className="text-muted-foreground font-medium normal-case tracking-normal truncate" data-testid={`decision-meta-${d.id}`}>· marked by <b className="text-foreground">{d.decision_by_name || "a teammate"}</b> · {timeAgo(d.decision_at)}</span>
          {unavailable && <span className="inline-flex items-center gap-1 rounded-full bg-muted text-foreground px-1.5 py-0.5 normal-case tracking-normal" data-testid={`decision-unavailable-${d.id}`}><VideoOff className="h-3 w-3" aria-hidden="true" />Video unavailable</span>}
          {d.saved === false && <span className="inline-flex items-center gap-1 rounded-full bg-muted text-foreground px-1.5 py-0.5 normal-case tracking-normal" data-testid={`decision-removed-${d.id}`}><BookmarkX className="h-3 w-3" aria-hidden="true" />Removed from Saved</span>}
          <ViewerAvatars viewers={viewers} />
        </div>
        <p className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words" data-testid={`decision-body-${d.id}`}>{d.body}</p>
        {unavailable ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
          <span className="truncate" data-testid={`decision-video-${d.id}`}>{d.video_title || "Titre inconnu"} <span className="opacity-70">(titre enregistré sur la note)</span></span>
          <span className="ml-auto" data-testid={`decision-metrics-${d.id}`}>Métriques indisponibles</span>
        </div>
        ) : (
        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
          <PlatformBadge platform={d.platform} />
          <MediaImage src={d.creator_avatar} alt="" name={d.creator_name || d.creator_handle} kind="avatar" demo={explicitDemoMedia(d)} className="h-5 w-5 rounded-full object-cover" />
          <span data-testid={`decision-creator-${d.id}`}><VideoAttribution video={{ ...d, id: d.video_id }} authorLink /></span>
          <span className="truncate">{d.creator_handle}</span>
          <span className="hidden sm:inline">·</span>
          <Link to={d.thread_path} className="truncate hover:underline" data-testid={`decision-video-${d.id}`}>{d.video_title}</Link>
          <span className="ml-auto font-mono" data-testid={`decision-metrics-${d.id}`}>{formatNumber(d.views)} views</span>
        </div>
        )}
        <div className="flex items-center gap-2 pt-1 flex-wrap">
          <Button asChild size="sm" variant="outline" data-testid={`decision-open-thread-${d.id}`}>
            <Link to={d.thread_path}><MessagesSquare className="h-3.5 w-3.5 mr-1.5" /> Open thread</Link>
          </Button>
          <FollowUpControls videoId={d.video_id} comment={d} members={members} canEdit={canEdit} onChange={(c) => onFollowUp(d.id, c)} currentUserId={currentUserId} />
          {canEdit && (
            <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-[hsl(var(--rose))]" onClick={() => onUnpin(d)} data-testid={`decision-unpin-${d.id}`}>
              <PinOff className="h-3.5 w-3.5 mr-1.5" /> Unpin
            </Button>
          )}
          {d.author_name && d.author_name !== d.decision_by_name && <span className="ml-auto text-[11px] text-muted-foreground">note by {d.author_name}</span>}
        </div>
      </div>
    </Card>
  );
}

export default function Decisions() {
  const { canEdit, user } = useAuth();
  const invalidateFollowUps = useInvalidateMyFollowUps();
  const { revision, viewersByDecision, setViewing } = useRealtime();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [creator, setCreator] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [member, setMember] = useState("all");
  const [status, setStatus] = useState(STATUSES.some((s) => s.key === searchParams.get("status")) ? searchParams.get("status") : "all");
  const mine = searchParams.get("assignee") === "me";
  const assignmentDestination = (assigned) => {
    const params = new URLSearchParams(searchParams);
    if (assigned) params.set("assignee", "me"); else params.delete("assignee");
    return { pathname: location.pathname, search: params.toString() ? `?${params}` : "", hash: location.hash };
  };
  const sectionTabs = SECTION_TABS.decisions.map((item) => ({ ...item, to: assignmentDestination(item.key === "follow-ups") }));
  const [range, setRange] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 250); return () => clearTimeout(t); }, [q]);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (creator !== "all") p.set("creator_id", creator);
    if (platform !== "all") p.set("platform", platform);
    if (member !== "all") p.set("marked_by", member);
    if (status !== "all") p.set("status", status);
    if (mine) p.set("assignee", "me");
    if (range === "custom") {
      if (from) p.set("date_from", new Date(from).toISOString());
      if (to) p.set("date_to", new Date(new Date(to).getTime() + 86400000 - 1).toISOString());
    } else if (range !== "all") {
      p.set("date_from", new Date(Date.now() - Number(range) * 86400000).toISOString());
    }
    if (debouncedQ.trim()) p.set("q", debouncedQ.trim());
    p.set("tz", Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
    return p.toString();
  }, [creator, platform, member, status, mine, range, from, to, debouncedQ]);

  const decisionsRead = useRead(`/decisions?${query}`, (d) => d && Array.isArray(d.items) && typeof d.board_total === "number" && typeof d.total === "number" && Array.isArray(d.members) && d.facets && ["creators", "platforms", "members"].every((k) => Array.isArray(d.facets[k])));
  const raw = decisionsRead.data;
  const data = useMemo(() => raw && { ...raw, ...splitItems(raw.items, isDecisionItem) }, [raw]);
  const unreadable = (data?.unreadable || 0) + (Array.isArray(data?.malformed) ? data.malformed.length : 0);
  const [unpinTarget, setUnpinTarget] = useState(null);
  // F-NAV-1: an unpinned card leaves the board with its Unpin button; focus then goes to the list, or to the page
  // when the list is gone (no decision left).
  const decisionsPage = useRef(null);
  const decisionsList = useRef(null);
  const unpinFocusFallback = () => {
    const target = decisionsList.current?.isConnected ? decisionsList.current : decisionsPage.current;
    if (target?.isConnected) focusTemporarily(target);
  };
  useEffect(() => { setUnpinTarget(null); }, [decisionsRead.key]);
  const setData = decisionsRead.setData;
  const load = () => decisionsRead.reload().catch(() => {});
  const reloadRead = decisionsRead.reload;
  const priorRevision = useRef(revision);
  useEffect(() => { if (priorRevision.current !== revision) { priorRevision.current = revision; reloadRead().catch(() => {}); } }, [revision, reloadRead]);

  // Report which decision cards are currently on screen (for "viewing this decision").
  useEffect(() => {
    if (!data || decisionsRead.status !== "success") { setViewing([]); return undefined; }
    const visible = new Set();
    const report = () => setViewing([...visible]);
    const obs = new IntersectionObserver((entries) => {
      let changed = false;
      entries.forEach((e) => {
        const id = e.target.getAttribute("data-decision-id");
        if (!id) return;
        if (e.isIntersecting) { if (!visible.has(id)) { visible.add(id); changed = true; } }
        else if (visible.has(id)) { visible.delete(id); changed = true; }
      });
      if (changed) report();
    }, { threshold: 0.5 });
    document.querySelectorAll("[data-decision-id]").forEach((el) => obs.observe(el));
    return () => { obs.disconnect(); setViewing([]); };
  }, [data, decisionsRead.status, setViewing]);
  // Stop reporting any viewed decisions when leaving the board.
  useEffect(() => () => setViewing([]), [setViewing]);

  // Deep-link focus: scroll to & highlight a specific follow-up (e.g. from an assignment toast).
  const focusId = searchParams.get("focus");
  const [highlightId, setHighlightId] = useState(null);
  const focusedScope = useRef(null);
  useEffect(() => { focusedScope.current = null; setHighlightId(null); }, [focusId, decisionsRead.key]);
  useEffect(() => {
    // Apply the deep link once per target/scope, not after background refreshes.
    if (!focusId || !data || decisionsRead.status !== "success" || focusedScope.current === decisionsRead.key) return;
    const el = document.querySelector(`[data-testid="decision-card-${focusId}"]`);
    if (!el) return;
    focusedScope.current = decisionsRead.key;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(focusId);
  }, [focusId, data, decisionsRead.key, decisionsRead.status]);
  useEffect(() => {
    if (!highlightId) return undefined;
    const t = setTimeout(() => setHighlightId(null), 2800);
    return () => clearTimeout(t);
  }, [highlightId, decisionsRead.key]);

  const unpin = async (d) => {
    if (!canEdit || decisionsRead.status !== "success") return;
    try {
      await api.patch(`/videos/${d.video_id}/comments/${d.id}/decision`, { is_decision: false });
      setData((prev) => prev && { ...prev, items: prev.items.filter((x) => x?.id !== d.id), total: prev.total - 1, board_total: prev.board_total - 1 });
      if (d.follow_up) invalidateFollowUps();
      load();
      toast.success("Decision unpinned — the note stays in its thread");
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const onFollowUp = (id, c) => {
    setData((prev) => {
      if (!prev) return prev;
      const items = prev.items.map((x) => (x?.id === id ? { ...x, follow_up: c.follow_up, follow_up_state: stateOf(c.follow_up) } : x));
      return { ...prev, items };
    });
    load();
  };

  const facets = data?.facets || { creators: [], platforms: [], members: [] };
  const hasFilters = creator !== "all" || platform !== "all" || member !== "all" || status !== "all" || mine || range !== "all" || q.trim();
  const reset = () => { setCreator("all"); setPlatform("all"); setMember("all"); setStatus("all"); navigate(assignmentDestination(false)); setRange("all"); setFrom(""); setTo(""); setQ(""); };

  return (
    <div className="space-y-6" data-testid="decisions-page" ref={decisionsPage}>
      <SectionTabs tabs={sectionTabs} activeKey={mine ? "follow-ups" : "decisions"} ariaLabel="Decisions sections" data-testid="section-tabs-decisions" />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Gavel className="h-6 w-6 text-[hsl(var(--amber))]" />
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Decision Board</h1>
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            Every note pinned as a decision in {user?.workspace_id ? "this workspace" : "your personal space"}, including videos removed from Saved — newest first, linked to its thread.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data && <span className="text-xs font-mono text-muted-foreground" data-testid="decisions-count">{data.total}{data.total !== data.board_total ? ` / ${data.board_total}` : ""} {data.board_total === 1 && data.total === 1 ? "decision" : "decisions"}</span>}
          <ExportButton path={`/export/decisions.csv?${query}`} testid="export-decisions-button" />
        </div>
      </div>

      {data?.board_total > 0 && data.summary && (
        <DecisionSummary
          summary={data.summary}
          filters={{ creator, member, platform, status }}
          onFilter={(key, value) => ({ creator: setCreator, member: setMember, platform: setPlatform, status: setStatus }[key])(value)}
        />
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="decisions-filters">
        <button
          onClick={() => navigate(assignmentDestination(!mine))}
          data-testid="decisions-assigned-to-me"
          aria-pressed={mine}
          className={classNames("inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors", mine ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground")}
        >
          <UserCheck className="h-3.5 w-3.5" /> Assigned to me
          {data?.my_follow_ups?.open > 0 && (
            <span className={classNames("rounded-full px-1.5 text-[9px] font-bold font-mono", data.my_follow_ups.overdue > 0 ? "bg-[hsl(var(--rose))] text-white" : "bg-primary/15 text-primary")} data-testid="decisions-assigned-to-me-count">{data.my_follow_ups.open}</span>
          )}
        </button>
        <div className="flex gap-1 rounded-lg bg-muted p-1" data-testid="decisions-status-pills">
          {STATUSES.map((s) => (
            <button key={s.key} onClick={() => setStatus(s.key)} className={pill(status === s.key)} data-testid={`decisions-status-${s.key}`}>
              {s.label}
              {s.key === "overdue" && data?.summary?.follow_ups?.overdue > 0 && <span className="ml-1 rounded-full bg-[hsl(var(--rose))] text-white px-1.5 text-[9px] font-bold">{data.summary.follow_ups.overdue}</span>}
            </button>
          ))}
        </div>
        {mine && status === "all" && <span className="text-[11px] text-muted-foreground" data-testid="decisions-mine-hint">Done items hidden — pick <b>Done</b> to see them</span>}
        <Select value={creator} onValueChange={setCreator}>
          <SelectTrigger className="w-48" data-testid="decisions-creator-select"><SelectValue placeholder="Creator" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All creators</SelectItem>
            {facets.creators.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} <span className="text-muted-foreground">· {platformLabel(c.platform)}</span></SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={platform} onValueChange={setPlatform}>
          <SelectTrigger className="w-36" data-testid="decisions-platform-select"><SelectValue placeholder="Platform" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All platforms</SelectItem>
            {facets.platforms.map((p) => <SelectItem key={p} value={p}>{platformLabel(p)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={member} onValueChange={setMember}>
          <SelectTrigger className="w-44" data-testid="decisions-member-select"><SelectValue placeholder="Marked by" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Marked by anyone</SelectItem>
            {facets.members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          {RANGES.map((r) => <button key={r.key} onClick={() => setRange(r.key)} className={pill(range === r.key)} data-testid={`decisions-range-${r.key}`}>{r.label}</button>)}
        </div>
        {range === "custom" && (
          <div className="flex items-center gap-1.5 text-xs" data-testid="decisions-custom-range">
            <CalendarRange className="h-4 w-4 text-muted-foreground" />
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-36" data-testid="decisions-date-from" />
            <span className="text-muted-foreground">→</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-36" data-testid="decisions-date-to" />
          </div>
        )}
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search decisions…" className="pl-8" data-testid="decisions-search-input" />
        </div>
        {hasFilters && <Button variant="ghost" size="sm" onClick={reset} data-testid="decisions-reset-filters">Clear</Button>}
      </div>

      {data?.truncated && <p className="text-xs text-muted-foreground" role="note" data-testid="decisions-truncated">Liste limitée aux {data.limit} décisions les plus récentes : des décisions plus anciennes existent et ne sont pas affichées.</p>}
      {unreadable > 0 && <p className="text-xs text-[hsl(var(--rose))]" role="alert" data-testid="decisions-malformed">{unreadable} décision(s) illisible(s) n’ont pas pu être affichées.</p>}
      <ReadState read={decisionsRead} scope="decisions" legacyEmptyId="decisions-empty" empty={!data?.items.length} filtered={!!data?.board_total} title={data?.board_total ? "Aucune décision pour ces filtres" : "Aucune décision enregistrée"} description={data?.board_total ? "Modifiez les filtres pour retrouver vos décisions." : user?.workspace_id ? (canEdit ? "Ouvrez le fil d’une vidéo sauvegardée, publiez une note puis marquez-la comme décision." : "Les décisions de votre équipe apparaîtront ici.") : "Consultez Saved. Pour utiliser le fil collaboratif, ouvrez un workspace."} primaryCta={data?.board_total ? { label: "Effacer les filtres", onClick: reset, testid: "decisions-empty-reset" } : { label: "Consulter Saved", to: "/saved" }} secondaryCta={!user?.workspace_id && !data?.board_total ? { label: "Créer un workspace", to: "/team", testid: "decisions-personal-workspace-cta" } : undefined}>
        <div className="space-y-3" data-testid="decisions-list" ref={decisionsList}>
          {data?.items.map((d) => {
            const memberById = Object.fromEntries((data.members || []).map((m) => [m.id, m]));
            const viewers = (viewersByDecision[d.id] || [])
              .filter((id) => id !== user?.id)
              .map((id) => memberById[id] || { id, name: "Teammate" });
            return <DecisionCard key={d.id} d={d} canEdit={canEdit} onUnpin={(item) => setUnpinTarget({ item, key: decisionsRead.key })} members={data.members || []} onFollowUp={onFollowUp} highlighted={d.id === highlightId} viewers={viewers} currentUserId={user?.id} />;
          })}
        </div>
      </ReadState>
      <ConfirmAction open={!!unpinTarget && unpinTarget.key === decisionsRead.key} onClose={() => setUnpinTarget(null)} testid="decision-unpin-dialog"
        contextKey={decisionsRead.key} restoreFocusFallback={unpinFocusFallback}
        onConfirm={() => { const t = unpinTarget; setUnpinTarget(null); return t && t.key === decisionsRead.key ? unpin(t.item) : undefined; }}
        title="Retirer la décision ?" confirmLabel="Retirer la décision">
        <p>La note reste dans son fil ; la mention « décision marquée par {unpinTarget?.item.decision_by_name || "un membre"} » sera effacée.</p>
        {unpinTarget?.item.follow_up && <p data-testid="decision-unpin-dialog-follow-up">Son suivi sera effacé aussi ({describeFollowUp(unpinTarget.item.follow_up)}), sans restauration possible ; seul un événement d’activité restera.</p>}
      </ConfirmAction>
    </div>
  );
}
